import { ServiceRegistry } from '../core/ServiceRegistry';
import { gameLogger } from '../../utils/GameLogger';

export type LifecyclePhase =
  | 'room_joined'
  | 'schema_first_sync'
  | 'player_created'
  | 'initial_snap'
  | 'first_input_sent'
  | 'first_input_acked'
  | 'settled'
  | 'custom';

export interface LifecycleSnapshot {
  timestamp: number;
  phase: LifecyclePhase;
  elapsed: number;
  // Player state
  localPosition: { x: number; y: number } | null;
  localVelocity: { x: number; y: number } | null;
  isGrounded: boolean | null;
  // Schema state (server authority)
  schemaPosition: { x: number; y: number } | null;
  schemaVelocity: { x: number; y: number } | null;
  schemaGrounded: boolean | null;
  // Network state
  roomId: string | null;
  sessionId: string | null;
  connected: boolean;
  pendingInputs: number;
  predictedStatesCount: number;
  currentSeq: number;
  // Diagnostics
  schemaRenderDist: number;
}

const MAX_ENTRIES = 50;

class PlayerLifecycleLoggerImpl {
  private timeline: LifecycleSnapshot[] = [];
  private firstTimestamp: number | null = null;

  log(phase: LifecyclePhase): void {
    const now = Date.now();
    if (this.firstTimestamp === null) this.firstTimestamp = now;

    const snapshot = this.captureSnapshot(phase, now);
    this.timeline.push(snapshot);
    if (this.timeline.length > MAX_ENTRIES) this.timeline.shift();

    // Console output for immediate visibility
    const pos = snapshot.localPosition
      ? `local:(${Math.round(snapshot.localPosition.x)},${Math.round(snapshot.localPosition.y)})`
      : 'local:n/a';
    const sPos = snapshot.schemaPosition
      ? `schema:(${Math.round(snapshot.schemaPosition.x)},${Math.round(snapshot.schemaPosition.y)})`
      : 'schema:n/a';
    gameLogger.info(
      'lifecycle',
      `[LIFECYCLE] ${phase.padEnd(20)} +${String(snapshot.elapsed).padStart(6)}ms  ${sPos}  ${pos}  pending:${snapshot.pendingInputs}  seq:${snapshot.currentSeq}  dist:${snapshot.schemaRenderDist.toFixed(0)}px`,
    );

    // Expose on window for console access
    if (typeof window !== 'undefined') {
      (window as any).__HELIX_LIFECYCLE__ = this;
    }
  }

  private captureSnapshot(phase: LifecyclePhase, now: number): LifecycleSnapshot {
    const registry = ServiceRegistry.getInstance();

    // Network state
    const nm = registry.get('NetworkManager') || (typeof window !== 'undefined' ? (window as any).nm : null);
    const room = nm?.getRoom?.() || nm?.room;
    const sessionId = room?.sessionId ?? null;
    const roomId = room?.roomId ?? room?.id ?? null;
    const connected = Boolean(room);

    // Schema state
    const playerSchema = sessionId ? room?.state?.players?.get?.(sessionId) : null;
    const schemaPosition = playerSchema && Number.isFinite(playerSchema.x)
      ? { x: playerSchema.x, y: playerSchema.y }
      : null;
    const schemaVelocity = playerSchema && Number.isFinite(playerSchema.velocityX)
      ? { x: playerSchema.velocityX, y: playerSchema.velocityY }
      : null;
    const schemaGrounded = playerSchema?.isGrounded ?? null;

    // Local player state
    const entitySystem = registry.get('EntitySystem');
    const player = entitySystem?.getPlayerManager?.()?.getPlayer?.();
    const localPosition = player?.position
      ? { x: player.position.x, y: player.position.y }
      : null;
    const localVelocity = player?.velocity
      ? { x: player.velocity.x, y: player.velocity.y }
      : null;
    const isGrounded = player?.isGrounded ?? null;

    // Input sync state
    const inputSync = typeof entitySystem?.getInputSyncSystem === 'function'
      ? entitySystem.getInputSyncSystem()
      : null;
    // pendingInputs / predictedStatesCount were server-reconciliation metrics;
    // under client-owned movement (2026-05-27) InputSyncSystem no longer tracks
    // them (the typed registry surfaced the dead getters). Logged as 0 to keep
    // the diagnostic shape stable; revive only if reconciliation returns.
    const pendingInputs = 0;
    const predictedStatesCount = 0;
    const currentSeq = inputSync?.getCurrentSeq?.() ?? 0;

    // Schema-render distance
    let schemaRenderDist = 0;
    if (schemaPosition && localPosition) {
      const dx = schemaPosition.x - localPosition.x;
      const dy = schemaPosition.y - localPosition.y;
      schemaRenderDist = Math.sqrt(dx * dx + dy * dy);
    }

    return {
      timestamp: now,
      phase,
      elapsed: now - (this.firstTimestamp ?? now),
      localPosition,
      localVelocity,
      isGrounded,
      schemaPosition,
      schemaVelocity,
      schemaGrounded,
      roomId,
      sessionId,
      connected,
      pendingInputs,
      predictedStatesCount,
      currentSeq,
      schemaRenderDist,
    };
  }

  getTimeline(): LifecycleSnapshot[] {
    return [...this.timeline];
  }

  getLatest(n: number): LifecycleSnapshot[] {
    return this.timeline.slice(-n);
  }

  getSummary(): string {
    if (this.timeline.length === 0) return 'No lifecycle events recorded.';
    const lines = this.timeline.map(s => {
      const pos = s.localPosition
        ? `(${Math.round(s.localPosition.x)},${Math.round(s.localPosition.y)})`
        : 'n/a';
      const sPos = s.schemaPosition
        ? `(${Math.round(s.schemaPosition.x)},${Math.round(s.schemaPosition.y)})`
        : 'n/a';
      return `${s.phase.padEnd(20)} +${String(s.elapsed).padStart(6)}ms  schema:${sPos.padEnd(12)} local:${pos.padEnd(12)} pending:${String(s.pendingInputs).padStart(4)}  seq:${String(s.currentSeq).padStart(4)}  dist:${s.schemaRenderDist.toFixed(0)}px`;
    });
    return lines.join('\n');
  }

  clear(): void {
    this.timeline = [];
    this.firstTimestamp = null;
  }
}

export const PlayerLifecycleLogger = new PlayerLifecycleLoggerImpl();
